import { createFormContext, zodResolver } from '@mantine/form'
import { useEffect, type HTMLAttributes } from 'react'

import {
  useMutationCreateUserAddress,
  useMutationUpdateUserAddress,
  useMutationUpdateUserLogged,
  useMutationUpdateUserTaxProfile,
} from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import {
  type CoreUserMeta,
  type CoreUser,
  type CoreUserTaxProfile,
  type CoreUserAddress,
  type ParameterID,
} from '@flecto/types'
import { useNotifications } from '@flecto/ui'

import { useBusinessInfoSchema } from '../hooks/useBusinessInfoSchema'

export const [FormProvider, useBusinessInfoFormContext, useForm] =
  createFormContext<Partial<BusinessInfoFormValues>>()

type BusinessInfoFormProps = HTMLAttributes<HTMLFormElement> & {
  user?: Partial<CoreUser>
  taxProfile?: Partial<CoreUserTaxProfile>
  onChangeLoading?: (loading: boolean) => void
}

export type BusinessInfoFormValues = {
  business_name: CoreUser['business_name']
  public_email: CoreUser['public_email']
  public_phone_number: CoreUser['public_phone_number']
  public_phone_number_country_code: CoreUserMeta['phone_number_country_code']
  vat_number: CoreUserTaxProfile['vat_number']
  legal_address: Partial<CoreUserAddress>
  business_category_id: ParameterID
  terms_and_conditions: CoreUser['terms_and_conditions']
}

export function BusinessInfoFormProvider({
  children,
  user,
  taxProfile,
  onChangeLoading,
  ...props
}: BusinessInfoFormProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_business' })
  const { showNotification } = useNotifications()
  const { mutateAsync: updateUser } = useMutationUpdateUserLogged()
  const { mutateAsync: updateTaxProfile } = useMutationUpdateUserTaxProfile()
  const { mutateAsync: createAddress } = useMutationCreateUserAddress()
  const { mutateAsync: updateAddress } = useMutationUpdateUserAddress()
  const { schema, initialValues, validateResponseErrors } =
    useBusinessInfoSchema()

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  async function saveBusinessInfo() {
    try {
      onChangeLoading?.(true)

      // There are cases where just created users does not have a
      // legal address yet, so we need to create it before
      if (!taxProfile?.legal_address && form.values.legal_address) {
        const createdAddress = await createAddress({
          address: { ...form.values.legal_address, legal: true },
        })

        form.values.legal_address.id = createdAddress.id
      }

      await Promise.all([
        updateUser({
          user: {
            business_name: form.values.business_name,
            public_email: form.values.public_email,
            public_phone_number: `${form.values.public_phone_number_country_code}${form.values.public_phone_number}`,
            business_category_id: form.values.business_category_id,
            terms_and_conditions: form.values.terms_and_conditions,
          },
        }),
        updateTaxProfile({
          taxProfile: {
            vat_number: form.values.vat_number,
          },
        }),
        updateAddress({
          address: {
            ...form.values.legal_address,
            legal: true,
          },
          addressId: form.values.legal_address?.id as ParameterID,
        }),
      ])

      showNotification({
        type: 'success',
        message: t('notifications.success'),
      })
    } catch (e) {
      showNotification({
        type: 'error',
        message: t('notifications.error'),
      })

      validateResponseErrors(e).forEach((error) =>
        form.setFieldError(error.field, error.message)
      )
    } finally {
      onChangeLoading?.(false)
    }
  }

  useEffect(() => {
    if (user) {
      const {
        business_name,
        public_email,
        public_phone_number,
        business_category_id,
        terms_and_conditions,
        meta,
      } = user
      form.setFieldValue('business_name', business_name)
      form.setFieldValue('public_email', public_email)
      form.setFieldValue('public_phone_number', public_phone_number)
      form.setFieldValue('business_category_id', business_category_id)
      form.setFieldValue('terms_and_conditions', terms_and_conditions)

      if (meta?.public_phone_number_country_code) {
        form.setFieldValue(
          'public_phone_number_country_code',
          meta?.public_phone_number_country_code
        )
      }
    }

    if (taxProfile) {
      const { vat_number, legal_address } = taxProfile
      form.setFieldValue('vat_number', vat_number)

      if (legal_address) {
        form.setFieldValue('legal_address', {
          id: legal_address.id,
          latitude: legal_address.latitude,
          longitude: legal_address.longitude,
          address_city: legal_address.address_city,
          address_region: legal_address.address_region,
          address_street: legal_address.address_street,
          address_postal_code: legal_address.address_postal_code,
          address_country_code: legal_address.address_country_code,
        })
      }
    }
  }, [taxProfile, user])

  return (
    <FormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveBusinessInfo())}>
        {children}
      </form>
    </FormProvider>
  )
}
